/**
 * Showcase Store 的 provide/inject 入口（ADR-0003，F02）。
 *
 * `main.ts` 调 `provideShowcase(createShowcaseStore())` 在 app 级注入；
 * 任意组件 `const store = useShowcase()` 取用，`store.state.xxx` 访问
 * 响应式状态（**禁止解构 state**，reactive 解构丢响应式）。
 *
 * **Debug-First**：`useShowcase()` 在未 provide 时抛错，不返 fallback
 * 对象——避免组件默默读到 undefined 再 NRE，让装配错误显式暴露。
 *
 * `SHOWCASE_STORE_KEY` 定义于此（非 showcase.ts）避免 showcase.ts →
 * use-showcase 循环依赖：use-showcase 只 import showcase 的类型。
 */
import { inject, type App, type InjectionKey, type Ref } from 'vue';

import type { ShowcaseStore } from '@/store/showcase';

export const SHOWCASE_STORE_KEY: InjectionKey<ShowcaseStore> = Symbol('ShowcaseStore');

/**
 * 全局 AnchorNav 激活锚点 id 的共享 ref（F13）。App.vue 持有并 provide，
 * HomePage 的 IntersectionObserver 写入，全局 `<AnchorNav>` 读取驱动首页
 * 区高亮。用 provide/inject 而非 store，避免把瞬时 UI 滚动状态耦合进
 * Showcase Store（store 只承载 aggregate 数据）。
 */
export const ACTIVE_ANCHOR_ID_KEY: InjectionKey<Ref<string>> = Symbol('ActiveAnchorId');

/** app 级注入 store（main.ts mount 前调用，走 app.provide 而非 setup 内 provide）。 */
export function provideShowcase(app: App, store: ShowcaseStore): void {
  app.provide(SHOWCASE_STORE_KEY, store);
}

/** 组件取 store。未 provide 抛错（Debug-First）。 */
export function useShowcase(): ShowcaseStore {
  const store = inject(SHOWCASE_STORE_KEY);
  if (store === undefined) {
    throw new Error(
      '[useShowcase] ShowcaseStore not provided — call provideShowcase() in main.ts',
    );
  }
  return store;
}
